import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { TModalProps } from "../components/others/Modal";
import LavorisService from "../services/LavorisService";
import { REDUCE_MODAL_DATA } from "../store/app/actionTypes";
import { RootState } from "../store/combineReducer";

type GetPropsModalReturn = TModalProps & {
  refreshTable?: () => void;
  closeModal: (...args: any[]) => void;
};

type CloseModalOptions = {
  goBack?: boolean
}

export function useModal() {
  const [params, setSearchParams] = useSearchParams();
  const { modalData } = useSelector((state: RootState) => state.app);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const openModal = (
    modalName: string,
    rowId?: string,
    modalData?: unknown,
  ) => {
    params.set(modalName, "active");
    if (rowId) params.set("rowId", rowId);
    setSearchParams(params);

    if (modalData) dispatch({ type: REDUCE_MODAL_DATA, payload: modalData });
  };

  const closeModal = (modalName: string, options?: CloseModalOptions) => {
    if (options?.goBack) {
      return navigate(-1);
    }
    if (params.has("rowId")) params.delete("rowId");
    if (params.get(modalName) === "active") params.delete(modalName);
    if (modalData) dispatch({ type: REDUCE_MODAL_DATA, payload: null });

    setSearchParams(params);
  };

  const isActive = (modalUrlTitle: string) =>
    params.get(modalUrlTitle) === "active";

  const getModalProps = (modalName: string): GetPropsModalReturn => ({
    show: isActive(modalName),
    closeModal: () => closeModal(modalName),
    urlTittle: modalName,
  });

  const getModalData = () => modalData;

  return {
    openModal,
    closeModal,
    isActive,
    getModalProps,
    getModalData,
  };
}

export function useModalData<T = any>(
  resource?: string,
  onFetched?: (item: any) => void,
): T {
  const { modalData } = useSelector((state: RootState) => state.app);
  const LavorisInstance = LavorisService.getInstance();
  const [params] = useSearchParams();
  const rowId = params.get("rowId");
  const dispatch = useDispatch();

  useEffect(() => {
    if (!modalData && !rowId)
      return console.error("There is not a rowId to fetch");
    if (!modalData && rowId && !resource)
      return console.error('Pass "resource" parameter to hook to do a fetch');

    if (!modalData && rowId && resource)
      LavorisInstance.Common.getOne(resource, rowId)
        .then((res) => {
          dispatch({ type: REDUCE_MODAL_DATA, payload: res });
          onFetched?.(res);
        })
        .catch(console.error);
  }, []);

  return modalData;
}
